import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Modal, Spinner, Alert, Card, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';

import PlanModal from '../components/subscriptions/PlanModal';
import FeatureModal from '../components/subscriptions/FeatureModal';
import PlanFeaturesTable from '../components/subscriptions/PlanFeaturesTable';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import { format_cents } from '../utils/Money';

import { get_billing_interval_label } from '../components/subscriptions/BillingIntervals';

import './css/subscription-editor.css';

// The subscription editor, for superadmins: the app's subscription plans in a tab per billing interval (Monthly and
// Annual), each with its features and their usage limits, added, edited and deleted in modals. Every request goes to this app's backend, which checks each field
// and passes the change on to the subscriptions service; it refuses to delete a plan with any subscriber, or a
// feature of one, and the editor shows its reason. Embedded, it is a tab of the superadmin page (SuperadminView): the page
// supplies the container and the title, so the editor shows neither.
function SubscriptionEditorView({embedded}){
	const { token, is_superadmin } = useAuth();

	const [plans, set_plans] = useState(null);
	const [options, set_options] = useState(null);
	const [load_error, set_load_error] = useState('');

	// The billing interval whose tab is open; a plan added there starts with it.
	const [active_interval, set_active_interval] = useState('month');

	// The open modal: editing_plan is undefined while the plan modal is closed and null when adding a plan;
	// feature_modal is null while closed, else { plan, feature } with feature null when adding one.
	const [editing_plan, set_editing_plan] = useState(undefined);
	const [feature_modal, set_feature_modal] = useState(null);

	// The plan or feature waiting for the delete to be confirmed: { kind, item, plan }.
	const [deleting, set_deleting] = useState(null);
	const [delete_running, set_delete_running] = useState(false);
	const [delete_error, set_delete_error] = useState('');

	const mounted_ref = useRef(true);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const load_plans = async()=>{
		try {
			set_load_error('');
			const response = await axios.get(Constants.SERVER_URL + '/api/admin/subscription_plans', { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			set_plans(response.data.plans);
			set_options(response.data.options);
		}
		catch (error) {
			console.log('SubscriptionEditorView/load_plans - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_load_error(get_user_error_message(error, 'The subscription plans could not be loaded.'));
				set_plans([]);
			}
		}
	};

	useEffect(()=>{
		mounted_ref.current = true;
		if(is_superadmin){
			load_plans();
		}

		return ()=>{
			mounted_ref.current = false;
		};
	}, [token, is_superadmin]);

	// Sends a change and reloads the plans. Throws an Error carrying the message to show, so the modal stays open.
	const send_change = async(method, path, data, fallback)=>{
		try {
			await axios({ method, url: Constants.SERVER_URL + path, data, headers: get_headers() });
		}
		catch (error) {
			console.log('SubscriptionEditorView/send_change - ERROR: ', describe_error(error));
			throw new Error(get_user_error_message(error, fallback));
		}

		await load_plans();
	};

	const save_plan = async(plan_data)=>{
		if(editing_plan){
			await send_change('put', '/api/admin/subscription_plan/' + editing_plan.id, plan_data, 'The plan could not be saved.');
		}
		else {
			await send_change('post', '/api/admin/subscription_plans', plan_data, 'The plan could not be added.');
		}

		set_editing_plan(undefined);
	};

	const save_feature = async(feature_data)=>{
		if(feature_modal.feature){
			await send_change('put', '/api/admin/subscription_feature/' + feature_modal.feature.id, feature_data, 'The feature could not be saved.');
		}
		else {
			await send_change('post', '/api/admin/subscription_features', feature_data, 'The feature could not be added.');
		}

		set_feature_modal(null);
	};

	const open_delete = (kind, item, plan)=>{
		set_delete_error('');
		set_deleting({ kind, item, plan });
	};

	const close_delete = ()=>{
		set_deleting(null);
		set_delete_error('');
	};

	const confirm_delete = async()=>{
		const path = (deleting.kind === 'plan') ? '/api/admin/subscription_plan/' : '/api/admin/subscription_feature/';
		try {
			set_delete_running(true);
			await send_change('delete', path + deleting.item.id, null, 'It could not be deleted.');
			set_delete_running(false);
			set_deleting(null);
		}
		catch (error) {
			set_delete_error(error.message);
			set_delete_running(false);
		}
	};

	const render_plan_modal = ()=>{
		if((editing_plan === undefined) || !options){
			return null;
		}

		return (
			<PlanModal
				plan={editing_plan}
				options={options}
				default_billing_interval={active_interval}
				on_save={save_plan}
				on_close={()=>{ set_editing_plan(undefined); }}/>
		);
	};

	const render_feature_modal = ()=>{
		if(!feature_modal || !options){
			return null;
		}

		return (
			<FeatureModal
				plan={feature_modal.plan}
				feature={feature_modal.feature}
				options={options}
				on_save={save_feature}
				on_close={()=>{ set_feature_modal(null); }}/>
		);
	};

	const render_delete_modal = ()=>{
		if(!deleting){
			return null;
		}

		const text = (deleting.kind === 'plan') ?
			'Delete the plan ' + deleting.item.name + ' and all its features?' :
			'Delete the feature ' + deleting.item.name + ' from ' + deleting.plan.name + '?';

		return (
			<Modal show={true} onHide={close_delete} centered>
				<Modal.Header closeButton>
					<Modal.Title>{(deleting.kind === 'plan') ? 'Delete Plan' : 'Delete Feature'}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>{text}</p>
					{delete_error && <Alert variant="danger">{delete_error}</Alert>}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" disabled={delete_running} onClick={close_delete}>
						Cancel
					</Button>
					<Button variant="danger" disabled={delete_running} onClick={confirm_delete}>
						{delete_running ? <Spinner animation="border" size="sm"/> : 'Delete'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_plan_facts = (plan)=>{
		const facts = [format_cents(plan.price_in_cents, plan.currency) + ' per ' + plan.billing_interval];
		facts.push(plan.is_active ? 'Active' : 'Inactive');
		facts.push(plan.has_subscribers ? 'Has subscribers' : 'No subscribers');
		return (
			<div className="subscription-plan-facts">{facts.join(' · ')}</div>
		);
	};

	const render_plan = (plan)=>{
		return (
			<Card key={plan.id} className="subscription-plan-card">
				<Row className="subscription-plan-header">
					<Col>
						<h5 className="subscription-plan-name">{plan.name}</h5>
						{render_plan_facts(plan)}
						{plan.description && <p className="subscription-plan-description">{plan.description}</p>}
					</Col>
					<Col className="subscription-plan-actions">
						<Button
							className="subscription-action-button"
							variant="outline-primary"
							size="sm"
							onClick={()=>{ set_feature_modal({ plan, feature: null }); }}>
							Add Feature
						</Button>
						<Button
							className="subscription-action-button"
							variant="outline-primary"
							size="sm"
							onClick={()=>{ set_editing_plan(plan); }}>
							Edit
						</Button>
						<Button
							className="subscription-action-button"
							variant="outline-danger"
							size="sm"
							onClick={()=>{ open_delete('plan', plan, null); }}>
							Delete
						</Button>
					</Col>
				</Row>
				<PlanFeaturesTable
					features={plan.features}
					options={options}
					on_edit={(feature)=>{ set_feature_modal({ plan, feature }); }}
					on_delete={(feature)=>{ open_delete('feature', feature, plan); }}/>
			</Card>
		);
	};

	// The plans billed at one interval, the content of its tab.
	const render_interval_plans = (billing_interval)=>{
		const interval_plans = plans.filter((plan)=>{ return plan.billing_interval === billing_interval; });
		if(interval_plans.length === 0){
			return (
				<p className="subscription-empty">{'No ' + get_billing_interval_label(billing_interval).toLowerCase() + ' plans yet.'}</p>
			);
		}

		return interval_plans.map((plan)=>{ return render_plan(plan); });
	};

	// A tab per billing interval the backend offers, Monthly and Annual.
	const render_plans = ()=>{
		if((plans === null) || !options){
			return (
				<div className="subscription-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			);
		}

		return (
			<Tabs
				className="subscription-editor-tabs"
				activeKey={active_interval}
				onSelect={(key)=>{ set_active_interval(key); }}>
				{options.billing_intervals.map((billing_interval)=>{
					return (
						<Tab key={billing_interval} eventKey={billing_interval} title={get_billing_interval_label(billing_interval)}>
							{render_interval_plans(billing_interval)}
						</Tab>
					);
				})}
			</Tabs>
		);
	};

	// The editor's container: the page's own container, or a plain block inside the superadmin page's tab.
	const render_container = (content)=>{
		if(embedded){
			return <div className="subscription-editor-view subscription-editor-embedded">{content}</div>;
		}

		return <Container className="view-container subscription-editor-view">{content}</Container>;
	};

	if(!is_superadmin){
		return render_container(
			<p className="subscription-empty">This page is for administrators only.</p>
		);
	}

	return (
		<React.Fragment>
			{render_container(
				<React.Fragment>
					<Row className="subscription-editor-header">
						<Col>
							{!embedded && <h1 className="subscription-editor-title">Subscriptions</h1>}
						</Col>
						<Col className="subscription-editor-header-actions">
							<Button variant="primary" disabled={!options} onClick={()=>{ set_editing_plan(null); }}>
								Add Plan
							</Button>
						</Col>
					</Row>

					{load_error && <Alert variant="danger">{load_error}</Alert>}

					{render_plans()}
				</React.Fragment>
			)}

			{render_plan_modal()}
			{render_feature_modal()}
			{render_delete_modal()}
		</React.Fragment>
	);
}

export default SubscriptionEditorView;
